// src/services/azurePlansService.js
// Azure Table Storage + Blob Storage Service für Projekt-Pläne (Grundrisse etc.).
// Muster: azurePendenzenService.js (projektbezogene Tabelle, PartitionKey = projectUuid,
// do/while-Paginierung, automatische Tabellenerstellung beim ersten Aufruf) und
// azureBlobService.js (Blob-Upload mit SAS-Token).
// US-PLAN-001: Datenmodell (Plan + Version), Upload, Planliste, Löschen.
// US-PLAN-006 (später): neue Version, superseded-Kennzeichnung, Fortschreibung.
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const TABLE_CONFIG = {
  storageAccount: "mfprojects",
  plansTable: "projectPlans",
  versionsTable: "projectPlanVersions",
  // Gleicher Account-SAS-Token wie azurePendenzenService.js / azurePersonsService.js
  // (Table-Service, konto-weit gültig, daher auch für neue Tabellen nutzbar).
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Eigener, nicht öffentlicher Blob-Container: Pläne können vertrauliche Bauunterlagen
// sein, daher bewusst NICHT wie project-images öffentlich lesbar, sondern über den
// (bereits im Projekt verwendeten) Blob-SAS-Token gelesen (buildPlanFileUrl).
const BLOB_CONFIG = {
  storageAccount: "mfprojects",
  containerName: "project-plans",
  sasToken:
    "sv=2024-11-04&ss=b&srt=co&sp=rwdlaciytfx&se=2035-10-02T19:07:10Z&st=2025-10-02T10:52:10Z&spr=https&sig=A8jWH2s8LkqmqiQjwjR8bL4sLjDKgKlE1HkDCZSlsYk%3D",
};

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const generatePlanId = () =>
  `plan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateVersionId = () =>
  `ver_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ALLOWED_TYPES = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
};

// Stellt sicher, dass beide Tabellen existieren (Anlage beim ersten Aufruf).
let tablesEnsured = false;
const ensureTablesExist = async () => {
  if (tablesEnsured) return true;
  try {
    const url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/Tables?${TABLE_CONFIG.sasToken}`;
    await Promise.all(
      [TABLE_CONFIG.plansTable, TABLE_CONFIG.versionsTable].map((TableName) =>
        fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json;odata=nometadata",
          },
          body: JSON.stringify({ TableName }),
        })
      )
    );
    tablesEnsured = true;
    return true;
  } catch (error) {
    console.error("Pläne: Tabellen konnten nicht angelegt werden:", error);
    return false;
  }
};

// Stellt sicher, dass der Blob-Container existiert (Anlage beim ersten Aufruf,
// analog ensureTablesExist – der Account-SAS-Token erlaubt Container-Erstellung).
let containerEnsured = false;
const ensureContainerExists = async () => {
  if (containerEnsured) return true;
  try {
    const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}?restype=container&${BLOB_CONFIG.sasToken}`;
    const response = await fetch(url, { method: "PUT" });
    if (response.ok || response.status === 409) {
      containerEnsured = true;
    }
    return containerEnsured;
  } catch (error) {
    console.error("Pläne: Container konnte nicht angelegt werden:", error);
    return false;
  }
};

// Öffentliche Lese-URL für eine Plandatei (Container ist nicht öffentlich, daher
// mit SAS-Token). Wird von PlanViewer/Plans für Anzeige & Download verwendet.
export const buildPlanFileUrl = (blobPath) =>
  `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;

const uploadBlob = async (blobPath, file) => {
  const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Plan-Blob-Upload-Fehler:", errorText);
    throw new Error("Die Plandatei konnte nicht hochgeladen werden.");
  }
};

const deleteBlob = async (blobPath) => {
  const url = `https://${BLOB_CONFIG.storageAccount}.blob.core.windows.net/${BLOB_CONFIG.containerName}/${blobPath}?${BLOB_CONFIG.sasToken}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok && response.status !== 404) {
    console.error("Plan-Blob-Delete-Fehler:", await response.text());
  }
};

// Seitenanzahl ermitteln: PDF via pdf.js, Bilder haben immer 1 "Seite".
const countPages = async (file, fileKind) => {
  if (fileKind !== "pdf") return 1;
  try {
    const buffer = await file.arrayBuffer();
    const doc = await pdfjsLib.getDocument({ data: buffer }).promise;
    return doc.numPages;
  } catch (error) {
    console.error("PDF-Seitenzahl konnte nicht ermittelt werden:", error);
    return 1;
  }
};

// Datei validieren: nur PDF/PNG/JPG. DWG & Co. werden mit Hinweistext abgelehnt.
export const validatePlanFile = (file) => {
  if (!file) throw new Error("Keine Datei ausgewählt.");
  if (!ALLOWED_TYPES[file.type]) {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext === "dwg") {
      throw new Error("DWG bitte vorab als PDF exportieren.");
    }
    throw new Error(
      "Ungültiges Format. Erlaubt sind PDF, PNG und JPG."
    );
  }
  return ALLOWED_TYPES[file.type];
};

const genericLoad = async (table, filter) => {
  let allEntries = [];
  let nextPartitionKey = null;
  let nextRowKey = null;
  do {
    let url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${table}()?${TABLE_CONFIG.sasToken}&$filter=${encodeURIComponent(
      filter
    )}`;
    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error:", errorText);
      throw new Error(`Azure Table Error: ${response.status}`);
    }
    const data = await response.json();
    allEntries = allEntries.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);
  return allEntries;
};

const mapPlan = (item) => ({
  id: item.RowKey,
  projectUuid: item.PartitionKey,
  name: item.Name || "",
  geschoss: item.Geschoss || "",
  // Massstab als Nenner (z.B. 100 für 1:100); null = nicht erfasst. Grundlage
  // für das Messen im PlanViewer (nur bei PDF, siehe dort).
  massstab: item.Massstab ? Number(item.Massstab) || null : null,
  aktiveVersionId: item.AktiveVersionId || "",
  erstelltAm: item.ErstelltAm || "",
  erstelltVon: item.ErstelltVon || "",
});

// "1:100" / "1 : 100" / "100" -> 100 ; ungültig -> null
export const parseMassstab = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;
  const denom = str.includes(":") ? str.split(":").pop() : str;
  const n = parseInt(String(denom).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const mapVersion = (item) => ({
  id: item.RowKey,
  planId: item.PartitionKey,
  revision: item.Revision || "",
  ausgabedatum: item.Ausgabedatum || "",
  blobPath: item.BlobPath || "",
  seitenanzahl: parseInt(item.Seitenanzahl, 10) || 1,
  superseded: item.Superseded === true || item.Superseded === "true",
  positionPruefen: item.PositionPruefen === true || item.PositionPruefen === "true",
  hochgeladenAm: item.HochgeladenAm || "",
  hochgeladenVon: item.HochgeladenVon || "",
});

// Alle Pläne eines Projekts laden.
export const getPlans = async (projectUuid) => {
  if (!projectUuid) return [];
  try {
    await ensureTablesExist();
    const entries = await genericLoad(
      TABLE_CONFIG.plansTable,
      `PartitionKey eq '${projectUuid}'`
    );
    return entries.map(mapPlan).sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error("Fehler beim Laden der Pläne:", error);
    throw new Error("Die Pläne konnten nicht geladen werden.");
  }
};

// Alle Versionen (Revisionen) eines Plans laden, älteste zuerst.
export const getPlanVersions = async (planId) => {
  if (!planId) return [];
  try {
    await ensureTablesExist();
    const entries = await genericLoad(
      TABLE_CONFIG.versionsTable,
      `PartitionKey eq '${planId}'`
    );
    return entries
      .map(mapVersion)
      .sort((a, b) => (a.hochgeladenAm || "").localeCompare(b.hochgeladenAm || ""));
  } catch (error) {
    console.error("Fehler beim Laden der Planversionen:", error);
    throw new Error("Die Planversionen konnten nicht geladen werden.");
  }
};

// Neuen Plan mit erster Version anlegen (Upload + beide Tabellenzeilen).
export const createPlan = async (
  projectUuid,
  { name, geschoss, revision, ausgabedatum, massstab },
  file,
  hochgeladenVon
) => {
  try {
    if (!name || !name.trim()) throw new Error("Bitte einen Namen erfassen.");
    const fileKind = validatePlanFile(file);
    await ensureTablesExist();
    await ensureContainerExists();

    const planId = generatePlanId();
    const versionId = generateVersionId();
    const now = new Date().toISOString();
    const ext = fileKind === "pdf" ? "pdf" : fileKind;
    const blobPath = `${projectUuid}/${planId}/${versionId}/original.${ext}`;

    await uploadBlob(blobPath, file);
    const seitenanzahl = await countPages(file, fileKind);

    const versionEntity = {
      PartitionKey: planId,
      RowKey: versionId,
      Revision: cleanValue(revision || "A"),
      Ausgabedatum: cleanValue(ausgabedatum),
      BlobPath: blobPath,
      Seitenanzahl: seitenanzahl,
      Superseded: false,
      PositionPruefen: false,
      HochgeladenAm: now,
      HochgeladenVon: cleanValue(hochgeladenVon),
    };
    const planEntity = {
      PartitionKey: projectUuid,
      RowKey: planId,
      Name: cleanValue(name.trim()),
      Geschoss: cleanValue(geschoss),
      Massstab: parseMassstab(massstab) || 0,
      AktiveVersionId: versionId,
      ErstelltAm: now,
      ErstelltVon: cleanValue(hochgeladenVon),
    };

    const versionsUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.versionsTable}?${TABLE_CONFIG.sasToken}`;
    const plansUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.plansTable}?${TABLE_CONFIG.sasToken}`;

    const versionResponse = await fetch(versionsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(versionEntity),
    });
    if (!versionResponse.ok) {
      throw new Error(`Azure Save Error: ${versionResponse.status}`);
    }

    const planResponse = await fetch(plansUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(planEntity),
    });
    if (!planResponse.ok) {
      throw new Error(`Azure Save Error: ${planResponse.status}`);
    }

    return { plan: mapPlan(planEntity), version: mapVersion(versionEntity) };
  } catch (error) {
    console.error("Fehler beim Anlegen des Plans:", error);
    throw new Error(error.message || "Der Plan konnte nicht gespeichert werden.");
  }
};

// Alle Pläne eines Projekts inkl. ihrer Versionen laden (Komfortfunktion für
// Ansichten, die beides brauchen, z.B. Plans.jsx und die aufklappbare
// Pendenz-Zeile in Pendenzen.jsx, US-PLAN-005).
export const getPlansWithVersions = async (projectUuid) => {
  const plans = await getPlans(projectUuid);
  return Promise.all(
    plans.map(async (plan) => ({ plan, versions: await getPlanVersions(plan.id) }))
  );
};

// Neue Version zu einem bestehenden Plan hochladen (US-PLAN-006): alte Version
// wird als "superseded" markiert (bleibt abrufbar), die neue wird aktive Version.
// Das Fortschreiben der Markierungen (positionPruefen=true) erfolgt separat über
// azurePendenzenMarkupsService.carryOverMarkupsToNewVersion (Aufrufer-Verantwortung,
// siehe Plans.jsx), damit dieser Service nicht von der Markup-Tabelle abhängt.
export const addPlanVersion = async (
  projectUuid,
  plan,
  { revision, ausgabedatum },
  file,
  hochgeladenVon
) => {
  try {
    const fileKind = validatePlanFile(file);
    await ensureTablesExist();
    await ensureContainerExists();

    const oldVersionId = plan.aktiveVersionId;
    const newVersionId = generateVersionId();
    const now = new Date().toISOString();
    const ext = fileKind === "pdf" ? "pdf" : fileKind;
    const blobPath = `${projectUuid}/${plan.id}/${newVersionId}/original.${ext}`;

    await uploadBlob(blobPath, file);
    const seitenanzahl = await countPages(file, fileKind);

    const newVersionEntity = {
      PartitionKey: plan.id,
      RowKey: newVersionId,
      Revision: cleanValue(revision || "B"),
      Ausgabedatum: cleanValue(ausgabedatum),
      BlobPath: blobPath,
      Seitenanzahl: seitenanzahl,
      Superseded: false,
      PositionPruefen: false,
      HochgeladenAm: now,
      HochgeladenVon: cleanValue(hochgeladenVon),
    };

    const versionsUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.versionsTable}?${TABLE_CONFIG.sasToken}`;
    const createResponse = await fetch(versionsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(newVersionEntity),
    });
    if (!createResponse.ok) {
      throw new Error(`Azure Save Error: ${createResponse.status}`);
    }

    // Alte Version als "superseded" markieren (bleibt abrufbar).
    if (oldVersionId) {
      const oldVersions = await getPlanVersions(plan.id);
      const oldVersion = oldVersions.find((v) => v.id === oldVersionId);
      if (oldVersion) {
        const oldEntity = {
          PartitionKey: plan.id,
          RowKey: oldVersionId,
          Revision: cleanValue(oldVersion.revision),
          Ausgabedatum: cleanValue(oldVersion.ausgabedatum),
          BlobPath: oldVersion.blobPath,
          Seitenanzahl: oldVersion.seitenanzahl,
          Superseded: true,
          PositionPruefen: oldVersion.positionPruefen,
          HochgeladenAm: oldVersion.hochgeladenAm,
          HochgeladenVon: oldVersion.hochgeladenVon,
        };
        const oldUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.versionsTable}(PartitionKey='${plan.id}',RowKey='${oldVersionId}')?${TABLE_CONFIG.sasToken}`;
        const oldResponse = await fetch(oldUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json;odata=nometadata",
          },
          body: JSON.stringify(oldEntity),
        });
        // Sonst blieben zwei nicht-superseded Versionen zurück (stiller Fehlzustand).
        if (!oldResponse.ok) {
          throw new Error(`Azure Update Error (superseded): ${oldResponse.status}`);
        }
      }
    }

    // Plan auf die neue aktive Version umsetzen (übrige Felder unverändert).
    const planEntity = {
      PartitionKey: projectUuid,
      RowKey: plan.id,
      Name: cleanValue(plan.name),
      Geschoss: cleanValue(plan.geschoss),
      Massstab: plan.massstab || 0,
      AktiveVersionId: newVersionId,
      ErstelltAm: cleanValue(plan.erstelltAm),
      ErstelltVon: cleanValue(plan.erstelltVon),
    };
    const planUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.plansTable}(PartitionKey='${projectUuid}',RowKey='${plan.id}')?${TABLE_CONFIG.sasToken}`;
    const planResponse = await fetch(planUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(planEntity),
    });
    if (!planResponse.ok) {
      throw new Error(`Azure Update Error: ${planResponse.status}`);
    }

    return {
      plan: mapPlan(planEntity),
      version: mapVersion(newVersionEntity),
      oldVersionId,
    };
  } catch (error) {
    console.error("Fehler beim Hochladen der neuen Planversion:", error);
    throw new Error(
      error.message || "Die neue Planversion konnte nicht gespeichert werden."
    );
  }
};

// Plan-Metadaten (Name, Geschoss, Massstab) bearbeiten. MERGE, damit die übrigen
// Felder (AktiveVersionId, ErstelltAm/Von) unangetastet bleiben.
export const updatePlan = async (projectUuid, planId, { name, geschoss, massstab }) => {
  try {
    const entity = {
      PartitionKey: projectUuid,
      RowKey: planId,
    };
    if (name != null) entity.Name = cleanValue(name.trim());
    if (geschoss != null) entity.Geschoss = cleanValue(geschoss);
    if (massstab !== undefined) entity.Massstab = parseMassstab(massstab) || 0;

    const url = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.plansTable}(PartitionKey='${projectUuid}',RowKey='${planId}')?${TABLE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      throw new Error(`Azure Update Error: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Plans:", error);
    throw new Error("Der Plan konnte nicht aktualisiert werden.");
  }
};

// Plan inkl. aller Versionen und Blob-Dateien löschen.
export const deletePlan = async (projectUuid, planId) => {
  try {
    const versions = await getPlanVersions(planId);
    for (const version of versions) {
      if (version.blobPath) {
        await deleteBlob(version.blobPath);
      }
      const versionUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.versionsTable}(PartitionKey='${planId}',RowKey='${version.id}')?${TABLE_CONFIG.sasToken}`;
      await fetch(versionUrl, { method: "DELETE", headers: { "If-Match": "*" } });
    }
    const planUrl = `https://${TABLE_CONFIG.storageAccount}.table.core.windows.net/${TABLE_CONFIG.plansTable}(PartitionKey='${projectUuid}',RowKey='${planId}')?${TABLE_CONFIG.sasToken}`;
    const response = await fetch(planUrl, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure Delete Error: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Plans:", error);
    throw new Error("Der Plan konnte nicht gelöscht werden.");
  }
};
